/**
 * Recommendations engine — public API surface (TASK 3-1).
 *
 * Only the five entry points listed in the work order are exported;
 * downstream code (UI, hooks, edge functions) MUST import from this
 * file rather than reaching into individual engine modules.
 *
 *   embedText            — produce a 384-dim vector for any text
 *   upsertProfileVector  — push the user's profile vector to Pinecone
 *   upsertJobVector      — push a job vector to Pinecone
 *   recommendJobs        — return top-K cached recommendations for a user
 *   explainMatch         — single-job RAG explanation for the
 *                          "Why this job for you?" panel (TASK 3-6)
 */

import { Embedder } from './embedder';
import {
  getRecommendationFor,
  getTopJobsForUser,
} from './recommendationEngine';
import {
  upsertJob,
  upsertProfile,
} from './vectorStore';
import type {
  JobVectorMeta,
  RecommendationResult,
  UserProfileVectorMeta,
} from '../types';

// ─── Embedding ────────────────────────────────────────────────────────────────

/**
 * Generate a 384-dim embedding for `text` using the in-browser
 * `Xenova/all-MiniLM-L6-v2` model.  Lazy-loads the WASM model on first
 * call (~23 MB, cached by the browser thereafter).
 */
export async function embedText(text: string): Promise<number[]> {
  return Embedder.getInstance().embed(text);
}

// ─── Vector store upserts ─────────────────────────────────────────────────────

/**
 * Upsert the current user's profile vector (index `ggj-b2c-profiles`).
 * The Pinecone API key never reaches the browser — the call goes via
 * the `ggj_b2c_pinecone` edge function (TASK 3-2).
 */
export async function upsertProfileVector(
  userId: string,
  vector: number[],
  metadata: Omit<UserProfileVectorMeta, 'user_id'> & Partial<Pick<UserProfileVectorMeta, 'user_id'>>,
): Promise<void> {
  if (!userId) {
    throw new Error('[recommendations] upsertProfileVector: missing userId');
  }
  if (!Array.isArray(vector) || vector.length === 0) {
    throw new Error('[recommendations] upsertProfileVector: empty vector');
  }
  return upsertProfile(userId, vector, { ...metadata, user_id: userId });
}

/**
 * Upsert a job vector (index `ggj-b2c-jobs`).  Typically called by an
 * ingestion job/edge function rather than the browser, but exposed
 * here for parity with the B2B engine.
 */
export async function upsertJobVector(
  jobId: string,
  vector: number[],
  metadata: Omit<JobVectorMeta, 'job_id'> & Partial<Pick<JobVectorMeta, 'job_id'>>,
): Promise<void> {
  if (!jobId) {
    throw new Error('[recommendations] upsertJobVector: missing jobId');
  }
  if (!Array.isArray(vector) || vector.length === 0) {
    throw new Error('[recommendations] upsertJobVector: empty vector');
  }
  return upsertJob(jobId, vector, { ...metadata, job_id: jobId });
}

// ─── Recommendations ──────────────────────────────────────────────────────────

/**
 * Return the user's top-K cached recommendations, ordered by score
 * descending.  This is the read path the dashboard "Recommended for
 * you" card consumes; it does NOT trigger fresh scoring — that is the
 * `match-jobs-for-user` edge function's job (TASK 3-5).
 */
export async function recommendJobs(
  userId: string,
  k = 10,
): Promise<RecommendationResult[]> {
  if (!userId) {
    throw new Error('[recommendations] recommendJobs: missing userId');
  }
  return getTopJobsForUser(userId, k);
}

/**
 * Return the single (user, job) recommendation row used by the
 * "Why this job for you?" RAG panel.  Returns `null` if the row hasn't
 * been computed yet — callers should fall back to invoking the
 * `explain-job-match` edge function in that case (TASK 3-6).
 */
export async function explainMatch(
  userId: string,
  jobId: string,
): Promise<RecommendationResult | null> {
  if (!userId || !jobId) {
    throw new Error('[recommendations] explainMatch: missing userId or jobId');
  }
  return getRecommendationFor(userId, jobId);
}

// ─── Type re-exports for downstream consumers ─────────────────────────────────

export type {
  JobVectorMeta,
  RecommendationResult,
  ScoreBreakdown,
  UserProfileVectorMeta,
  MatchBand,
} from '../types';
